import React, { useMemo } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

import addYears from "date-fns/addYears";
import format from "date-fns/format";
import sortBy from "lodash/sortBy";
import { Entity } from "models/entity";
import numeral from "numeral";
import { useToggle } from "usehooks-ts";

import { SKSpinner } from "components/customElements/skSpinner";
import Table from "components/Table";
import { useApi } from "hooks/useApi";
import { baseFetcher } from "utils/baseFetcher";

import { consensusDividendPaymentsUrl } from "./utils";

const DEFAULT_START_DATE = addYears(new Date(), -5);
const DEFAULT_END_DATE = new Date();

const renderHeader = (title: string) => {
  return <div className="whitespace-nowrap">{title}</div>;
};
const renderNumberHeader = (title: string) => {
  return <div className="whitespace-nowrap">{title}</div>;
};
const renderDateCell = (value: string, _record: any) => (
  <div className="whitespace-nowrap">{format(new Date(value), "dd/MM/yy")}</div>
);
const renderNumberCell = (value: string, _record: any) => (
  <div className="text-right">{numeral(value).format("0,0.[00]")}</div>
);
const renderTextCell = (value: string, _record: any) => (
  <div className="whitespace-nowrap">{value}</div>
);
const renderTypeCell = (value: string, record: any) => (
  <OverlayTrigger overlay={<Tooltip>{record.type}</Tooltip>}>
    <span className="whitespace-nowrap cursor-pointer">{value || "N/A"}</span>
  </OverlayTrigger>
);

interface DividendPayoutsTableProps {
  entity: Entity;
  startDate?: Date;
  endDate?: Date;
}

const DividendPayoutsTable = ({
  entity,
  startDate = DEFAULT_START_DATE,
  endDate = DEFAULT_END_DATE,
}: DividendPayoutsTableProps) => {
  const [showMore, toggleShowMore] = useToggle(false);

  const { data: dividendPayments, isValidating: isValidatingDividendPayments } =
    useApi(
      consensusDividendPaymentsUrl(entity.id, startDate, endDate),
      baseFetcher,
      {
        revalidateOnFocus: false,
        revalidateOnReconnect: false,
      }
    );

  const hasMore = useMemo(
    () => dividendPayments?.graph?.date?.length > 10,
    [dividendPayments]
  );

  const dataSource = useMemo(() => {
    const graph = dividendPayments?.graph;
    if (!graph?.date?.length) return [];

    const { date: dates, value: values, _meta: meta } = graph;

    let rs = dates
      .map((date: string, index: number) => {
        const value = values?.[index];
        const m = meta?.[index];
        if (value == null || !m) return null;

        return {
          date: Date.parse(date),
          dividendPayment: value,
          announceDate: m.announceDate,
          exDate: m.exDate,
          frequency: m.frequency,
          payDate: m.payDate,
          recordDate: m.recordDate,
          reportedAmount: m.reportedAmount,
          reportedCurrency: m.reportedCurrency,
          supplemental: m.supplemental,
          type: m.type,
          unadjValue: m.unadjValue,
        };
      })
      .filter(
        (
          item: any
        ): item is {
          date: number;
          dividendPayment: number;
          announceDate: string;
          exDate: string;
          frequency: string;
          payDate: string;
          recordDate: string;
          reportedAmount: number;
          reportedCurrency: string;
          supplemental: boolean;
          type: string;
          unadjValue: number;
        } => item !== null
      );

    rs = sortBy(rs, "date");

    return showMore ? rs : rs.slice(0, 10);
  }, [dividendPayments?.graph, showMore]);

  const columns: any[] = [
    {
      key: "announceDate",
      title: "An-D",
      dataIndex: "announceDate",
      renderHeader,
      render: renderDateCell,
    },
    {
      key: "exDate",
      title: "Ex-D",
      dataIndex: "exDate",
      renderHeader,
      render: renderDateCell,
    },
    {
      key: "recordDate",
      title: "Rec-D",
      dataIndex: "recordDate",
      renderHeader,
      render: renderDateCell,
    },
    {
      key: "payDate",
      title: "Pay-D",
      dataIndex: "payDate",
      renderHeader,
      render: renderDateCell,
    },
    {
      key: "reportedCurrency",
      title: "FX",
      dataIndex: "reportedCurrency",
      renderHeader,
    },
    {
      key: "reportedAmount",
      title: "Amount",
      dataIndex: "reportedAmount",
      renderHeader: renderNumberHeader,
      render: renderNumberCell,
    },
    {
      key: "unadjValue",
      title: "Adj Amt",
      dataIndex: "unadjValue",
      renderHeader: renderNumberHeader,
      render: renderNumberCell,
    },
    {
      key: "frequency",
      title: "Frequency",
      dataIndex: "frequency",
      renderHeader,
      render: renderTextCell,
    },
    {
      key: "supplemental",
      title: "Type",
      dataIndex: "supplemental",
      renderHeader,
      render: renderTypeCell,
    },
  ];

  if (isValidatingDividendPayments) return <SKSpinner showLoading={false} />;

  return (
    <div className="mb-[20px]">
      <div className="text-[16px] text-center font-bold pb-[15px]">
        Dividend Payout Schedule
      </div>
      <div className="overflow-x-auto no-scrollbar">
        <Table
          columns={columns}
          dataSource={dataSource}
          tableClassName="!mb-[15px]"
        ></Table>
      </div>
      {hasMore && (
        <div className="flex justify-center">
          <div
            className="text-smart-teal text-[14px] cursor-pointer"
            onClick={toggleShowMore}
          >
            Show {showMore ? "Less" : "More"}
          </div>
        </div>
      )}
    </div>
  );
};

export default DividendPayoutsTable;
